import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface ChartSeries {
  name: string;
  data: (number | null)[];
}

interface MonthlyClosingsData {
  categories: string[];
  years: number[];
  series: ChartSeries[];
}

interface Props {
  data: MonthlyClosingsData;
  title?: string;
  neighborhoodName?: string;
  propertyTypeLabel?: string;
}

const FONT = "'Montserrat Light', 'Montserrat', sans-serif";
const FONT_SIZE = '11pt';
const FONT_STYLE = { fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: 'normal' as const };

const SERIES_COLORS = ['#FF0000', '#ED7D31', '#FFC000', '#5B9BD5', '#70AD47'];
const SERIES_WIDTHS = [1, 1, 2, 2.5, 4];
const LABEL_FONT_SIZES = ['11pt', '11pt', '11pt', '12pt', '14pt'];
const MAX_VISIBLE_LABELS = 15;

export const MonthlyClosingsChart = React.forwardRef<HighchartsReact.RefObject, Props>(
  ({ data, title = 'Sales By Month', neighborhoodName = '', propertyTypeLabel = '' }, ref) => {
    const subtitle = `${neighborhoodName} | 5 year ${propertyTypeLabel} Monthly Sales Trend`;

    const lastIndex = data.categories.length - 1;
    const labelStep = Math.max(1, Math.ceil(data.categories.length / MAX_VISIBLE_LABELS));
    const closingsLabelFormatter: Highcharts.DataLabelsFormatterCallbackFunction = function () {
      if (this.y == null) return '';
      if (labelStep > 1 && this.index % labelStep !== 0 && this.index !== lastIndex) return '';
      return Highcharts.numberFormat(this.y, 0);
    };
    const options: Highcharts.Options = {
      chart: {
        type: 'line',
        height: 560,
        marginTop: 160,
        style: { fontFamily: FONT, fontSize: FONT_SIZE }
      },
      title: {
        text: title,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: '500', color: '#000000' }
      },
      subtitle: {
        text: subtitle,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: '500', color: '#66635F' }
      },
      xAxis: {
        categories: data.categories,
        title: { text: 'Month', style: FONT_STYLE },
        labels: { style: FONT_STYLE, rotation: -45 }
      },
      yAxis: {
        title: { text: 'Number of Closings', style: FONT_STYLE },
        labels: { style: FONT_STYLE },
        min: 0
      },
      legend: {
        itemStyle: FONT_STYLE
      },
      tooltip: {
        shared: true,
        valueSuffix: ' closings',
        style: FONT_STYLE
      },
      plotOptions: {
        line: {
          marker: { enabled: false },
          connectNulls: false,
          dataLabels: { allowOverlap: false, formatter: closingsLabelFormatter }
        }
      },
      series: data.series.map((s, i) => ({
        type: 'line' as const,
        name: s.name,
        data: s.data,
        color: SERIES_COLORS[i],
        lineWidth: SERIES_WIDTHS[i],
        dataLabels: {
          enabled: true,
          allowOverlap: false,
          style: {
            fontFamily: FONT,
            fontSize: LABEL_FONT_SIZES[i],
            fontWeight: 'normal',
            textOutline: 'none',
            color: SERIES_COLORS[i]
          }
        }
      })),
      credits: { enabled: false }
    };

    return <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />;
  }
);
